import {
  BarChart3Icon,
  DrillIcon,
  DropletIcon,
  FactoryIcon,
  LayersIcon,
  MapIcon,
  MicroscopeIcon,
  MountainIcon,
  RadarIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  result: string;
  note: string;
  icon: LucideIcon;
  tags: string;
};

export const modules: Module[] = [
  {
    number: '01',
    title: 'Fundamentos de los Yacimientos de Hidrocarburos',
    subtitle: 'Comprender qué son los yacimientos y cómo se originan',
    description:
      'Concepto de yacimiento de hidrocarburos, origen del petróleo y del gas natural, formación y acumulación de hidrocarburos, condiciones geológicas necesarias, migración primaria y secundaria, rocas generadoras, reservorio y sello, conceptos fundamentales de petróleo y gas, clasificación general de los yacimientos, introducción al ciclo de vida de un yacimiento. Todo el contexto que necesitás para hablar con autoridad desde el primer día.',
    result:
      'Tus alumnos entienden por qué existen los yacimientos, qué procesos geológicos permiten la acumulación de petróleo y gas, cuál es la importancia de los yacimientos en Argentina, y por qué cada descubrimiento representa millones de años de procesos geológicos.',
    note: 'Contenido completo con recursos didácticos',
    icon: DropletIcon,
    tags: 'ORIGEN DEL PETRÓLEO Y GAS · FORMACIÓN Y ACUMULACIÓN · ROCAS GENERADORAS, RESERVORIO Y SELLO · MIGRACIÓN DE HIDROCARBUROS · CICLO DE VIDA DE UN YACIMIENTO',
  },
  {
    number: '02',
    title: 'Geología del Petróleo y Sistemas Petroleros',
    subtitle: 'Pasar de la teoría a comprender sistemas complejos',
    description:
      'Fundamentos de geología del petróleo, cuencas sedimentarias, materia orgánica y generación de hidrocarburos, maduración térmica, migración de petróleo y gas, elementos de un sistema petrolero, roca generadora, reservorio y sello, trampas y sincronización geológica, análisis conceptual de sistemas petroleros. Con casos argentinos.',
    result:
      'Tus alumnos pueden explicar cómo se forma un sistema petrolero, comprender la relación entre generación, migración y acumulación, analizar la sincronización geológica y reconocer que los yacimientos no son accidentes sino productos de sistemas bien definidos.',
    note: 'Matrices y análisis sistémicos listos para aplicar',
    icon: LayersIcon,
    tags: 'SISTEMAS PETROLEROS · MADURACIÓN TÉRMICA · MIGRACIÓN · SINCRONIZACIÓN GEOLÓGICA · CUENCAS SEDIMENTARIAS',
  },
  {
    number: '03',
    title: 'Rocas Reservorio y Propiedades del Yacimiento',
    subtitle: 'Conocer las características que permiten el almacenamiento de hidrocarburos',
    description:
      'Concepto de roca reservorio, tipos de rocas reservorio, porosidad, permeabilidad, saturación de fluidos, propiedades petrofísicas, heterogeneidad del reservorio, distribución de petróleo, gas y agua, relación entre las propiedades de la roca y el comportamiento del yacimiento. Especialmente desarrollado para la realidad argentina.',
    result:
      'Tus alumnos entienden qué características hacen que una roca sea capaz de almacenar y transmitir hidrocarburos, cómo varía la porosidad y permeabilidad, cómo se distribuyen los fluidos, y por qué estas propiedades determinan el potencial de un yacimiento.',
    note: 'Con guía de análisis petrofísico y caracterización',
    icon: MicroscopeIcon,
    tags: 'POROSIDAD Y PERMEABILIDAD · SATURACIÓN DE FLUIDOS · PROPIEDADES PETROFÍSICAS · HETEROGENEIDAD · DISTRIBUCIÓN DE FLUIDOS',
  },
  {
    number: '04',
    title: 'Trampas, Acumulaciones y Tipos de Yacimientos',
    subtitle: 'Comprender cómo quedan atrapados los hidrocarburos',
    description:
      'Formación de acumulaciones, concepto y funcionamiento de una trampa, trampas estructurales, trampas estratigráficas, trampas combinadas, sellos geológicos, yacimientos de petróleo y gas, yacimientos convencionales y no convencionales, shale y reservorios de baja permeabilidad, comparación entre diferentes tipos de acumulaciones. Con casos reales de yacimientos argentinos.',
    result:
      'Tus alumnos saben qué es una trampa geológica, cómo funcionan los diferentes tipos de trampas, cuál es la diferencia entre yacimientos convencionales y no convencionales, y pueden analizar escenarios reales como Vaca Muerta.',
    note: 'Casos reales de trampas argentinas',
    icon: MountainIcon,
    tags: 'TRAMPAS ESTRUCTURALES · TRAMPAS ESTRATIGRÁFICAS · SELLOS GEOLÓGICOS · YACIMIENTOS CONVENCIONALES Y NO CONVENCIONALES · VACA MUERTA',
  },
  {
    number: '05',
    title: 'Exploración de Hidrocarburos',
    subtitle: 'Cómo se estudian las áreas con potencial hidrocarburífero',
    description:
      'Objetivos de la exploración, etapas del proceso exploratorio, estudios geológicos, métodos geofísicos, introducción a la sísmica, identificación de prospectos, evaluación del potencial hidrocarburífero, riesgo e incertidumbre geológica, pozos exploratorios, del descubrimiento a la evaluación. Aplicable a la realidad exploratoria argentina.',
    result:
      'Tus alumnos comprenden cómo las empresas exploradoras identifican áreas de potencial, qué métodos utilizan, cómo se define un prospecto, cómo se gestiona la incertidumbre, y por qué la exploración es el primer paso para encontrar yacimientos.',
    note: 'Plan de exploración escolar incluido',
    icon: RadarIcon,
    tags: 'ETAPAS DE EXPLORACIÓN · MÉTODOS GEOFÍSICOS · SÍSMICA · PROSPECTOS EXPLORATORIOS · RIESGO GEOLÓGICO',
  },
  {
    number: '06',
    title: 'Caracterización y Evaluación de Yacimientos',
    subtitle: 'Integra la información para comprender el potencial de un yacimiento',
    description:
      'Caracterización geológica, caracterización petrofísica, propiedades de rocas y fluidos, continuidad y heterogeneidad, información obtenida de pozos, evaluación del reservorio, estimación conceptual de recursos, incertidumbre en la evaluación, integración de información geológica y técnica, toma de decisiones sobre el desarrollo.',
    result:
      'Tus alumnos entienden cómo se reúne toda la información de un descubrimiento, cómo se caracteriza un yacimiento, cómo se estiman los recursos, cómo se valida la información y cómo se toma la decisión de desarrollar un campo.',
    note: 'Guía completa de evaluación de yacimientos',
    icon: BarChart3Icon,
    tags: 'CARACTERIZACIÓN GEOLÓGICA · PROPIEDADES PETROFÍSICAS · ESTIMACIÓN DE RECURSOS · INCERTIDUMBRE EN EVALUACIÓN · TOMA DE DECISIONES',
  },
  {
    number: '07',
    title: 'Perforación, Terminación y Desarrollo de Yacimientos',
    subtitle: 'Cómo un descubrimiento comienza a convertirse en un yacimiento desarrollado',
    description:
      'Introducción a la perforación, tipos generales de pozos, pozos verticales y horizontales, conceptos fundamentales de terminación, integridad del pozo, desarrollo de un yacimiento, planificación conceptual del desarrollo, yacimientos convencionales y no convencionales, relación entre perforación, reservorio y producción, seguridad y consideraciones ambientales.',
    result:
      'Tus alumnos saben qué tipos de pozos existen, cómo se perfora, cómo se termina un pozo, cómo se planifica el desarrollo de un campo, y comprenden que el desarrollo es una integración de múltiples disciplinas.',
    note: 'Secuencias de desarrollo listas para aplicar',
    icon: DrillIcon,
    tags: 'TIPOS DE POZOS · PERFORACIÓN · TERMINACIÓN · PLANIFICACIÓN DE DESARROLLO · INTEGRIDAD DEL POZO',
  },
  {
    number: '08',
    title: 'Producción y Gestión del Yacimiento',
    subtitle: 'Qué sucede durante la etapa productiva',
    description:
      'Fundamentos de producción, comportamiento productivo del yacimiento, petróleo, gas y agua durante la producción, evolución de la presión, declinación de la producción, recuperación de hidrocarburos, gestión integral del yacimiento, campos maduros, optimización conceptual de la producción, etapas finales y cierre.',
    result:
      'Tus alumnos entienden cómo produce un yacimiento, cómo varía con el tiempo, cómo se optimiza la producción, cómo se gestiona un campo a lo largo de su vida útil, y por qué la gestión del yacimiento es crucial para maximizar la recuperación.',
    note: '100 casos prácticos con resolución docente',
    icon: FactoryIcon,
    tags: 'COMPORTAMIENTO PRODUCTIVO · DECLINACIÓN DE PRODUCCIÓN · RECUPERACIÓN DE HIDROCARBUROS · GESTIÓN INTEGRAL · CAMPOS MADUROS',
  },
  {
    number: '09',
    title: '🇦🇷 Yacimientos de Hidrocarburos en Argentina y su Enseñanza',
    subtitle: 'Conecta los conocimientos con el contexto argentino',
    description:
      'Panorama de los hidrocarburos en Argentina, principales cuencas sedimentarias argentinas, Cuenca Neuquina, introducción a Vaca Muerta, Golfo San Jorge, Cuenca Austral, Cuenca Cuyana y del Noroeste, yacimientos convencionales y no convencionales, importancia energética y territorial, estrategias para enseñar yacimientos, actividades, análisis de casos y propuestas educativas.',
    result:
      'Tus alumnos conocen las principales cuencas argentinas, comprenden la importancia de Vaca Muerta, pueden analizar yacimientos específicos argentinos, entienden el contexto energético del país, y desarrollan proyectos significativos contextualizados.',
    note: 'Proyectos y simulaciones listos para implementar',
    icon: MapIcon,
    tags: 'CUENCAS ARGENTINAS · VACA MUERTA · YACIMIENTOS CONVENCIONALES Y NO CONVENCIONALES · IMPORTANCIA ENERGÉTICA · GESTIÓN TERRITORIAL',
  },
];
